import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import {
    RestoIdolAnalysisData,
    RestoIdolEquippedIdol,
    RestoIdolFightRow,
    RestoIdolHealerRow,
    RestoIdolHealingTally,
    RestoIdolTankGroup,
    RestoIdolTankRow,
} from '../types';
import TbcErrorBanner from '../tbcAnalyzer/components/TbcErrorBanner';
import TbcFightBreakdownTable from '../tbcAnalyzer/components/TbcFightBreakdownTable';
import TbcLogUrlPhase from '../tbcAnalyzer/components/TbcLogUrlPhase';
import TbcPhaseHeader from '../tbcAnalyzer/components/TbcPhaseHeader';
import TbcSelectionPhase from '../tbcAnalyzer/components/TbcSelectionPhase';
import TbcSimpleBreakdownTable, { TbcSimpleBreakdownColumn } from '../tbcAnalyzer/components/TbcSimpleBreakdownTable';
import { AggregatedHealer } from '../tbcAnalyzer/types';
import { useTbcAnalyzerSession } from '../tbcAnalyzer/useTbcAnalyzerSession';
import { TbcReportRouteProps, useTbcReportRoute } from '../tbcAnalyzer/useTbcReportRoute';
import { dedupeRemovedLabel, fightFilterLabel, formatDuration, formatNumber } from '../tbcAnalyzer/utils';
import HelpTip from './HelpTip';

const ANALYZE_TIMEOUT_MS = 10 * 60 * 1000;

function isRestoDruid(player: AggregatedHealer): boolean {
    const classLabel = player.class.toLowerCase();
    if (classLabel.indexOf('druid') === -1) {
        return false;
    }
    if (classLabel.indexOf('restoration') !== -1) {
        return true;
    }
    if (
        classLabel.indexOf('balance') !== -1 ||
        classLabel.indexOf('feral') !== -1 ||
        classLabel.indexOf('guardian') !== -1
    ) {
        return false;
    }
    return player.role === 'healer';
}

function classColorClass(classLabel: string): string {
    const first = classLabel.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
    return `player-cell ${first}`;
}

function signedNumber(value: number, decimals = 0): string {
    const formatted = formatNumber(Math.abs(value), decimals);
    if (value > 0) {
        return `+${formatted}`;
    }
    if (value < 0) {
        return `−${formatted}`;
    }
    return formatted;
}

function deltaClass(value: number): string {
    if (value > 0) {
        return 'idol-delta-positive';
    }
    if (value < 0) {
        return 'idol-delta-negative';
    }
    return '';
}

function hps(effective: number, durationSeconds: number): number {
    if (durationSeconds <= 0) {
        return 0;
    }
    return effective / durationSeconds;
}

function sumTally(rows: RestoIdolFightRow[], pick: (row: RestoIdolFightRow) => RestoIdolHealingTally): number {
    return rows.reduce((sum, row) => sum + pick(row).effectiveHealing, 0);
}

function vsEmeraldQueen(healer: RestoIdolHealerRow): number {
    return healer.ravenGoddess.effectiveHealing - healer.emeraldQueen.effectiveHealing;
}

function equippedIdolLabel(idol: RestoIdolEquippedIdol | undefined): string {
    if (idol === 'raven_goddess') {
        return 'Idol of the Raven Goddess';
    }
    if (idol === 'emerald_queen') {
        return 'Idol of the Emerald Queen';
    }
    if (idol === 'neither') {
        return 'neither ToL idol';
    }
    if (idol === 'mixed') {
        return 'mixed idols across fights';
    }
    return 'idol not in the log';
}

function tankGroupLabel(group: RestoIdolTankGroup): string {
    if (group === 'in') {
        return 'In group';
    }
    if (group === 'mixed') {
        return 'Mixed';
    }
    return 'Not in group';
}

function emptyTally(): RestoIdolHealingTally {
    return { hits: 0, crits: 0, rawHealing: 0, effectiveHealing: 0 };
}

function tankTableColumns(duration: number): TbcSimpleBreakdownColumn<RestoIdolTankRow>[] {
    return [
        {
            key: 'name',
            header: 'Tank',
            render: (tank) => <span className={classColorClass(tank.class)}>{tank.name}</span>,
        },
        {
            key: 'class',
            header: 'Class',
            render: (tank) => tank.class,
        },
        {
            key: 'group',
            header: 'ToL group',
            render: (tank) => tankGroupLabel(tank.group),
        },
        {
            key: 'received',
            header: 'Received',
            render: (tank) => formatNumber(tank.received.effectiveHealing),
        },
        {
            key: 'ifIn',
            header: 'If in ToL group',
            render: (tank) => formatNumber(tank.ifInGroup.effectiveHealing),
        },
        {
            key: 'hps',
            header: 'If-in HPS',
            render: (tank) => formatNumber(hps(tank.ifInGroup.effectiveHealing, duration), 1),
        },
        {
            key: 'pct',
            header: '% of incoming',
            render: (tank) => {
                const total = tank.totalReceived ?? 0;
                if (total <= 0) {
                    return '—';
                }
                return `${formatNumber((tank.ifInGroup.effectiveHealing / total) * 100, 1)}%`;
            },
        },
    ];
}

const RestoIdolAnalyzer: React.FC<TbcReportRouteProps> = ({ basePath, reportId }) => {
    const session = useTbcAnalyzerSession({ roleFilter: 'healers' });
    const [result, setResult] = useState<RestoIdolAnalysisData | null>(null);
    const [expandedHealer, setExpandedHealer] = useState<string | null>(null);
    const resultsRef = useRef<HTMLDivElement>(null);
    const reportRoute = useTbcReportRoute<RestoIdolAnalysisData>({
        basePath,
        reportId,
        session,
        onRestore: (restored) => {
            setResult(restored);
            setExpandedHealer(null);
        },
        onLeave: () => {
            setResult(null);
            setExpandedHealer(null);
        },
    });

    const restoDruids = useMemo(() => {
        return session.players.filter(isRestoDruid).sort((left, right) => left.name.localeCompare(right.name));
    }, [session.players]);

    useEffect(() => {
        if (restoDruids.length === 0) {
            if (session.selectedPlayerName) {
                session.setSelectedPlayerName('');
            }
            return;
        }
        if (!restoDruids.some((player) => player.name === session.selectedPlayerName)) {
            session.setSelectedPlayerName(restoDruids[0].name);
        }
    }, [restoDruids, session.selectedPlayerName, session.setSelectedPlayerName]);

    const handleProcessAnalysis = async () => {
        const validationError = session.validateBeforeAnalyze();
        if (validationError) {
            session.setError(
                validationError === 'Please select a healer for analysis'
                    ? 'Please select a resto druid for analysis'
                    : validationError
            );
            return;
        }

        session.setLoading(true);
        session.setError(null);
        setResult(null);
        setExpandedHealer(null);
        const wclProgressId = session.beginWclProgress();

        try {
            const response = await axios.post(
                '/api/attendance/analyze-resto-idols',
                {
                    ...session.sharedRequestBody,
                    wclProgressId,
                },
                { timeout: ANALYZE_TIMEOUT_MS }
            );
            setResult(response.data);
            session.setPhase('results');
            reportRoute.publish(response.data);
        } catch (err: any) {
            console.error('Error analyzing resto idols:', err);
            session.setError(
                err.response?.data?.error || err.response?.data?.details || 'Failed to analyze logs. Please try again.'
            );
        } finally {
            session.endWclProgress();
            session.setLoading(false);
        }
    };

    const handleBackToUrl = () => {
        session.handleBackToUrl();
        setResult(null);
        setExpandedHealer(null);
    };

    const filterLabel = result ? fightFilterLabel(result.fightFilter, session.bosses, result.fightFilterLabel) : '';
    const raidDelta = result ? result.ravenGoddess.effectiveHealing - result.emeraldQueen.effectiveHealing : 0;
    const ravenWins = raidDelta > 0;
    const duration = result?.totalFightDurationSeconds ?? 0;

    const idolHelp = (
        <HelpTip label="How Tree of Life value is calculated" wide>
            <h4>Tree of Life aura</h4>
            <p>
                TBC Tree of Life increases healing received on your party by <strong>25% of your Spirit</strong>. If
                Idol of the Raven Goddess is equipped, that amount also includes +{formatNumber(44)}. Incoming heals use
                the same TBC coefficients as Stat Values (direct crits × 1.5). Only the idol you actually wore is used
                for this total — the two idols are not both applied.
            </p>
            <h4>Tanks</h4>
            <p>
                In-group tanks use heals they received while they had <em>this</em> druid&apos;s ToL aura. Tanks who
                were not in your group are valued as if they had been in your party whenever you were in Tree form.
            </p>
            <h4>Idol of the Emerald Queen</h4>
            <p>
                +{formatNumber(88)} bonus healing on Lifebloom <strong>ticks</strong> (not the bloom), on every target
                you HoT. Extra healing is 88 × the Lifebloom HoT coefficient × stacks, using the same TBC coefficients
                as Stat Values (Empowered Rejuvenation included).
            </p>
            <h4>Idol of the Raven Goddess</h4>
            <p>
                +{formatNumber(44)} bonus healing <strong>received</strong> by anyone currently affected by{' '}
                <em>this</em> druid&apos;s Tree of Life party aura (not a second resto druid&apos;s group). The idol
                comparison below is what that +44 is worth vs Emerald Queen, separate from the Spirit-based ToL total.
            </p>
            <p>
                Party membership comes from Tree of Life aura events sourced by the selected druid. 45-yard range is not
                modeled beyond that aura. Effective healing is discounted by each event&apos;s overheal.
            </p>
        </HelpTip>
    );

    return (
        <div className="item-set-calculator">
            {session.phase !== 'results' && (
                <>
                    <h2 className="heading-with-help">
                        Tree of Life
                        {idolHelp}
                    </h2>
                    <p className="analyzer-description">
                        Value this resto druid&apos;s Tree of Life aura (Spirit and equipped idol) and compare Idol of
                        the Emerald Queen with Idol of the Raven Goddess.
                    </p>
                </>
            )}

            <TbcErrorBanner error={session.error} />

            {session.phase === 'url' && !reportRoute.restoring && (
                <TbcLogUrlPhase
                    logUrlsText={session.logUrlsText}
                    loading={session.loading}
                    loadingLabel="Loading healers..."
                    onLogUrlsChange={session.setLogUrlsText}
                    onLoadLogs={() => {
                        void session.handleLoadLogs();
                    }}
                    wclSnapshot={session.wclSnapshot}
                />
            )}

            {session.phase === 'selection' && (
                <TbcSelectionPhase
                    loadedLogUrls={session.loadedLogUrls}
                    players={restoDruids}
                    bosses={session.bosses}
                    selectedPlayerName={session.selectedPlayerName}
                    fightFilter={session.fightFilter}
                    selectedEncounterIds={session.selectedEncounterIds}
                    minDurationSeconds={session.minDurationSeconds}
                    loading={session.loading}
                    analyzeButtonLabel="Analyze Tree of Life"
                    showMinDurationFilter
                    minDurationHelpText="Ignores fights shorter than this."
                    title="Select Druid and Fights"
                    playerCountLabel="Resto druids found:"
                    playerSectionTitle="Select Resto Druid"
                    playerPlaceholder="Select a resto druid..."
                    playerHelp={
                        <HelpTip label="Which druid">
                            <p>
                                Pick the resto druid whose Tree of Life to value. Aura and Raven Goddess only count from
                                this player, even if another resto druid is in the raid.
                            </p>
                        </HelpTip>
                    }
                    onSelectedPlayerChange={session.setSelectedPlayerName}
                    onFightFilterChange={session.setFightFilter}
                    onToggleEncounterId={session.toggleEncounterId}
                    onToggleAllEncounterIds={session.toggleAllEncounterIds}
                    onToggleRaidEncounterIds={session.toggleRaidEncounterIds}
                    onMinDurationChange={session.setMinDurationSeconds}
                    onAnalyze={() => {
                        void handleProcessAnalysis();
                    }}
                    onBack={handleBackToUrl}
                    wclSnapshot={session.wclSnapshot}
                />
            )}

            {session.phase === 'results' && result && (
                <div className="results-phase" ref={resultsRef}>
                    <TbcPhaseHeader
                        exportTarget={resultsRef}
                        title="Tree of Life"
                        backLabel="← Back to Selection"
                        onBack={reportRoute.leave}
                        help={idolHelp}
                    />

                    <div className="mp5-showcase">
                        <div className="mp5-card">
                            <div className="mp5-header">
                                <h3>Tree of Life</h3>
                                <div className="mp5-value">
                                    <span className="mp5-number">
                                        {formatNumber((result.treeOfLife ?? emptyTally()).effectiveHealing)}
                                    </span>
                                    <span className="mp5-label">effective extra healing</span>
                                </div>
                                <div className="mp5-value" style={{ marginTop: '12px' }}>
                                    <span className="mp5-number">
                                        {formatNumber(
                                            hps((result.treeOfLife ?? emptyTally()).effectiveHealing, duration),
                                            1
                                        )}
                                    </span>
                                    <span className="mp5-label">HPS</span>
                                </div>
                            </div>
                            <div className="mp5-subtitle">
                                {result.avgSpirit != null
                                    ? `${formatNumber(result.avgSpirit, 0)} Spirit × ${formatNumber((result.treeOfLifeSpiritRatio ?? 0.25) * 100, 0)}%`
                                    : 'Spirit not in the log'}
                                {result.equippedIdol === 'raven_goddess' ? ' + 44 Raven Goddess' : ''}
                                {result.avgTreeOfLifeBonusHealing != null
                                    ? ` = +${formatNumber(result.avgTreeOfLifeBonusHealing, 0)} received`
                                    : ''}
                                {' · '}
                                {equippedIdolLabel(result.equippedIdol)}
                                {result.equippedIdol === 'emerald_queen' ? ' — Raven Goddess +44 is not included' : ''}
                                {result.equippedIdol === 'mixed' ? ' — each fight uses the idol worn on that pull' : ''}
                            </div>
                        </div>
                    </div>

                    {(result.tanks ?? []).length > 0 && (
                        <>
                            <h4>Tanks in your Tree of Life group</h4>
                            <p className="analyzer-description">
                                Received is extra healing while they had this druid&apos;s ToL aura. If in ToL group
                                also counts heals during Tree form when they did not have the aura (range or a fight
                                they were not in your party). % of incoming is that extra divided by all modeled healing
                                they received on these fights.
                            </p>
                            <TbcSimpleBreakdownTable
                                rows={(result.tanks ?? []).filter((tank) => tank.group !== 'out')}
                                rowKey={(tank) => tank.name}
                                emptyMessage="No tanks were in this druid's Tree of Life group."
                                columns={tankTableColumns(duration)}
                            />

                            <h4>Tanks not in your group</h4>
                            <p className="analyzer-description">
                                Extra healing if these tanks had been in your party whenever you were in Tree form. % of
                                incoming is that extra versus the healing they actually received.
                            </p>
                            <TbcSimpleBreakdownTable
                                rows={(result.tanks ?? []).filter((tank) => tank.group === 'out')}
                                rowKey={(tank) => tank.name}
                                emptyMessage="Every tank spent some time in this druid's Tree of Life group."
                                columns={tankTableColumns(duration)}
                            />
                        </>
                    )}

                    <h4>Idol comparison</h4>
                    <p className="analyzer-description">
                        What the two idols are worth on this log. Tree of Life above already includes Raven Goddess +44
                        only if that idol was equipped.
                    </p>

                    <div className="mp5-showcase">
                        <div className="mp5-card">
                            <div className="mp5-header">
                                <h3>Emerald Queen</h3>
                                <div className="mp5-value">
                                    <span className="mp5-number">
                                        {formatNumber(result.emeraldQueen.effectiveHealing)}
                                    </span>
                                    <span className="mp5-label">effective extra healing</span>
                                </div>
                                <div className="mp5-value" style={{ marginTop: '12px' }}>
                                    <span className="mp5-number">
                                        {formatNumber(hps(result.emeraldQueen.effectiveHealing, duration), 1)}
                                    </span>
                                    <span className="mp5-label">HPS</span>
                                </div>
                            </div>
                            <div className="mp5-subtitle">
                                +{result.emeraldQueenBonusHealing} on your Lifebloom ticks ·{' '}
                                {formatNumber(result.emeraldQueen.hits)} ticks ·{' '}
                                {formatNumber(result.emeraldQueen.rawHealing)} before overheal
                            </div>
                        </div>
                        <div className="mp5-card">
                            <div className="mp5-header">
                                <h3>Raven Goddess</h3>
                                <div className="mp5-value">
                                    <span className="mp5-number">
                                        {formatNumber(result.ravenGoddess.effectiveHealing)}
                                    </span>
                                    <span className="mp5-label">effective extra healing</span>
                                </div>
                                <div className="mp5-value" style={{ marginTop: '12px' }}>
                                    <span className="mp5-number">
                                        {formatNumber(hps(result.ravenGoddess.effectiveHealing, duration), 1)}
                                    </span>
                                    <span className="mp5-label">HPS</span>
                                </div>
                            </div>
                            <div className="mp5-subtitle">
                                +{result.ravenGoddessBonusHealing} received on this druid&apos;s ToL party ·{' '}
                                {formatNumber(result.ravenGoddess.hits)} modeled hits ·{' '}
                                {formatNumber(result.ravenGoddess.rawHealing)} before overheal
                            </div>
                        </div>
                    </div>

                    <div className={`idol-verdict${raidDelta === 0 ? '' : ravenWins ? ' is-raven' : ' is-queen'}`}>
                        {raidDelta === 0 ? (
                            <>
                                <strong>Tie</strong>
                                <span> — both idols give the same extra healing on these fights</span>
                            </>
                        ) : (
                            <>
                                <strong>{ravenWins ? 'Raven Goddess' : 'Emerald Queen'}</strong>
                                <span>
                                    {' '}
                                    is ahead. Swapping to {ravenWins ? 'Emerald Queen' : 'Raven Goddess'} would lose{' '}
                                    {formatNumber(Math.abs(raidDelta))} effective healing (
                                    {formatNumber(Math.abs(hps(raidDelta, duration)), 1)} HPS) for the raid.
                                </span>
                            </>
                        )}
                    </div>

                    <p className="analyzer-description">
                        {result.selectedPlayer
                            ? `${result.selectedPlayer.name} (${result.selectedPlayer.class})`
                            : 'Unknown druid'}
                        {filterLabel ? ` · ${filterLabel}` : ''} · {result.reportIds.length} log
                        {result.reportIds.length === 1 ? '' : 's'} · {result.fightsProcessed} fight
                        {result.fightsProcessed === 1 ? '' : 's'} · {formatDuration(duration)}
                        {dedupeRemovedLabel(result.duplicatesRemoved)}
                    </p>
                    <p className="analyzer-description">
                        {result.partyAuraFound
                            ? `Tree of Life party (this druid): ${result.partyMembers.join(', ') || 'none'}`
                            : 'No Tree of Life party aura from this druid was found. Raven Goddess is only counted on the druid while in Tree form — a second resto’s group is not included.'}
                        {result.unmodeledHits > 0
                            ? ` · ${formatNumber(result.unmodeledHits)} ToL-party hits had no coefficient and were skipped.`
                            : ''}
                    </p>

                    <h4>Healers</h4>
                    <p className="analyzer-description">
                        Click a row for a spell breakdown. Emerald Queen only shows on you (all of your Lifebloom ticks,
                        including targets outside your Tree of Life party). Other healers gain Raven Goddess value when
                        they heal someone with this druid&apos;s ToL aura. The vs Emerald Queen column is what the raid
                        gains or loses on that healer; those numbers sum to the verdict above.
                    </p>
                    <TbcSimpleBreakdownTable
                        rows={result.healers}
                        rowKey={(healer) => healer.name}
                        emptyMessage="No modeled healing landed on this druid's Tree of Life party."
                        onRowClick={(healer) => {
                            setExpandedHealer((current) => (current === healer.name ? null : healer.name));
                        }}
                        expandedRowKey={expandedHealer}
                        renderExpandedRow={(healer) => (
                            <div style={{ padding: '8px 12px 16px' }}>
                                <TbcSimpleBreakdownTable
                                    rows={healer.spells}
                                    rowKey={(spell) => String(spell.spellId)}
                                    emptyMessage="No spells for this healer."
                                    columns={[
                                        {
                                            key: 'spell',
                                            header: 'Spell',
                                            render: (spell) => (
                                                <span title={`Spell ID ${spell.spellId}`}>{spell.spellName}</span>
                                            ),
                                        },
                                        {
                                            key: 'eq',
                                            header: 'Emerald Queen',
                                            render: (spell) => formatNumber(spell.emeraldQueen.effectiveHealing),
                                        },
                                        {
                                            key: 'rg',
                                            header: 'Raven Goddess',
                                            render: (spell) => formatNumber(spell.ravenGoddess.effectiveHealing),
                                        },
                                        {
                                            key: 'hits',
                                            header: 'Hits',
                                            render: (spell) =>
                                                formatNumber(
                                                    Math.max(spell.emeraldQueen.hits, spell.ravenGoddess.hits)
                                                ),
                                        },
                                    ]}
                                />
                            </div>
                        )}
                        columns={[
                            {
                                key: 'name',
                                header: 'Healer',
                                render: (healer) => (
                                    <span className={classColorClass(healer.class)}>
                                        {healer.name}
                                        {healer.isSelectedDruid ? ' (you)' : ''}
                                    </span>
                                ),
                            },
                            {
                                key: 'class',
                                header: 'Class',
                                render: (healer) => healer.class,
                            },
                            {
                                key: 'eq',
                                header: 'Emerald Queen',
                                render: (healer) => formatNumber(healer.emeraldQueen.effectiveHealing),
                            },
                            {
                                key: 'rg',
                                header: 'Raven Goddess',
                                render: (healer) => formatNumber(healer.ravenGoddess.effectiveHealing),
                            },
                            {
                                key: 'delta',
                                header: 'vs Emerald Queen',
                                render: (healer) => {
                                    const delta = vsEmeraldQueen(healer);
                                    return <span className={deltaClass(delta)}>{signedNumber(delta)}</span>;
                                },
                            },
                            {
                                key: 'hps',
                                header: 'RG HPS',
                                render: (healer) =>
                                    formatNumber(hps(healer.ravenGoddess.effectiveHealing, duration), 1),
                            },
                        ]}
                    />

                    {(result.receivers ?? []).length > 0 && (
                        <>
                            <h4>Receivers</h4>
                            <p className="analyzer-description">
                                Players who received extra healing from this druid&apos;s Tree of Life (Raven Goddess).
                                A second resto druid&apos;s group will not appear here.
                            </p>
                            <TbcSimpleBreakdownTable
                                rows={result.receivers ?? []}
                                rowKey={(receiver) => receiver.name}
                                emptyMessage="No Raven Goddess healing was attributed."
                                columns={[
                                    {
                                        key: 'name',
                                        header: 'Player',
                                        render: (receiver) => (
                                            <span className={classColorClass(receiver.class)}>{receiver.name}</span>
                                        ),
                                    },
                                    {
                                        key: 'class',
                                        header: 'Class',
                                        render: (receiver) => receiver.class,
                                    },
                                    {
                                        key: 'rg',
                                        header: 'Raven Goddess',
                                        render: (receiver) => formatNumber(receiver.ravenGoddess.effectiveHealing),
                                    },
                                    {
                                        key: 'hits',
                                        header: 'Hits',
                                        render: (receiver) => formatNumber(receiver.ravenGoddess.hits),
                                    },
                                    {
                                        key: 'hps',
                                        header: 'RG HPS',
                                        render: (receiver) =>
                                            formatNumber(hps(receiver.ravenGoddess.effectiveHealing, duration), 1),
                                    },
                                ]}
                            />
                        </>
                    )}

                    <h4>Fights</h4>
                    <TbcFightBreakdownTable
                        fights={result.fights}
                        rowKey={(fight) => `${fight.reportId}:${fight.fightId}`}
                        columns={[
                            {
                                key: 'fight',
                                header: 'Fight',
                                render: (fight) => fight.fightName,
                                aggregate: () => 'Boss total',
                            },
                            {
                                key: 'dur',
                                header: 'Dur',
                                render: (fight) => formatDuration(fight.durationSeconds),
                                aggregate: (rows) =>
                                    formatDuration(rows.reduce((sum, fight) => sum + fight.durationSeconds, 0)),
                            },
                            {
                                key: 'tol',
                                header: 'ToL %',
                                render: (fight) => `${formatNumber(fight.treeOfLifeUptimePercent, 0)}%`,
                            },
                            {
                                key: 'tolh',
                                header: 'ToL extra',
                                render: (fight) => formatNumber((fight.treeOfLife ?? emptyTally()).effectiveHealing),
                                aggregate: (rows) =>
                                    formatNumber(
                                        rows.reduce(
                                            (sum, fight) => sum + (fight.treeOfLife ?? emptyTally()).effectiveHealing,
                                            0
                                        )
                                    ),
                            },
                            {
                                key: 'lb',
                                header: 'LB ticks',
                                render: (fight) => formatNumber(fight.lifebloomTicks),
                                aggregate: (rows) =>
                                    formatNumber(rows.reduce((sum, fight) => sum + fight.lifebloomTicks, 0)),
                            },
                            {
                                key: 'stacks',
                                header: 'Avg stacks',
                                render: (fight) =>
                                    fight.lifebloomTicks > 0 ? formatNumber(fight.lifebloomAvgStacks, 2) : '—',
                            },
                            {
                                key: 'eq',
                                header: 'Emerald Queen',
                                render: (fight) => formatNumber(fight.emeraldQueen.effectiveHealing),
                                aggregate: (rows) => formatNumber(sumTally(rows, (fight) => fight.emeraldQueen)),
                            },
                            {
                                key: 'rg',
                                header: 'Raven Goddess',
                                render: (fight) => formatNumber(fight.ravenGoddess.effectiveHealing),
                                aggregate: (rows) => formatNumber(sumTally(rows, (fight) => fight.ravenGoddess)),
                            },
                            {
                                key: 'delta',
                                header: 'RG − EQ',
                                render: (fight) => {
                                    const delta =
                                        fight.ravenGoddess.effectiveHealing - fight.emeraldQueen.effectiveHealing;
                                    return <span className={deltaClass(delta)}>{signedNumber(delta)}</span>;
                                },
                                aggregate: (rows) => {
                                    const delta =
                                        sumTally(rows, (fight) => fight.ravenGoddess) -
                                        sumTally(rows, (fight) => fight.emeraldQueen);
                                    return <span className={deltaClass(delta)}>{signedNumber(delta)}</span>;
                                },
                            },
                        ]}
                    />
                </div>
            )}
        </div>
    );
};

export default RestoIdolAnalyzer;
